import React, { useMemo } from 'react';

import LinearReport from 'c-components/HiddenReport/ReportTypes/LinearReport';
import DSPReport from 'c-components/HiddenReport/ReportTypes/DSPReport';
import CustomReport from 'c-components/HiddenReport/ReportTypes/CustomReport';
import type { PcaReportFilters } from 'c-components/HiddenReport/reportFilters';
import { parsePcaReportFilters } from 'c-components/HiddenReport/reportFilters';

export interface Payload {
    campaign_id: number;
    jwt: string;
    powerpoint_template: ReportTemplate;
    skin_id: number;
    environment: Environment;
    email: string;
    language: 'GB' | 'US';
    start_date?: string;
    end_date?: string;
    third_color?: string;
    /** UL-8220: analytics filters every report query is narrowed by. */
    filters?: PcaReportFilters;
}

type LinearPayload = Payload & { powerpoint_template: 'Linear_report' };
type DspPayload = Payload & { powerpoint_template: 'DSP_report' };
type CustomPayload = Payload & { powerpoint_template: 'custom' };

type Environment = 'local' | 'production' | 'staging';
type ReportTemplate = 'Linear_report' | 'DSP_report' | 'custom';
function tryJSON(s: string): any | null {
    try {
        return JSON.parse(s);
    } catch {
        return null;
    }
}

function fromBase64Utf8(b64: string): string {
    try {
        const bin = atob(b64);
        // convert binary string → UTF-8
        return decodeURIComponent(
            Array.prototype.map
                .call(bin, (c: string) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join(''),
        );
    } catch {
        return '';
    }
}

function safeDecodeURIComponent(s: string): string {
    try {
        return decodeURIComponent(s);
    } catch {
        return s;
    }
}

function normalizeEnvironment(env?: string): string {
    const v = (env ?? '').toLowerCase();
    if (v === 'production' || v === 'prod') return 'production';
    if (v === 'staging' || v === 'stage') return 'staging';
    if (v === 'development' || v === 'dev' || v === 'local') return 'local';
    return 'production';
}

function normalizePayload(p: any): Payload {
    return {
        ...p,
        environment: normalizeEnvironment(p?.environment),
        language: p?.language === 'US' ? 'US' : 'GB',
        filters: parsePcaReportFilters(p?.filters),
    } as Payload;
}

function decodePayloadFromQuery(): Payload | null {
    const qs = new URLSearchParams(window.location.search);
    const query: Record<string, string> = {};
    qs.forEach((value, key) => {
        query[key] = value;
    });

    const rawB64 = query.payload_b64;
    if (rawB64) {
        const v0 = rawB64;
        const v1 = rawB64.replace(/ /g, '+');
        const v2 = safeDecodeURIComponent(rawB64);
        const v3 = v2.replace(/ /g, '+');
        const candidates = [v0, v1, v2, v3];

        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < candidates.length; i++) {
            const json = fromBase64Utf8(candidates[i] || '');
            const parsed = tryJSON((json || '').trim());
            if (parsed) return normalizePayload(parsed);
        }
        return null;
    }
    const raw = query.payload;
    if (!raw) return null;

    const cA = raw;
    const cB = safeDecodeURIComponent(raw);
    const candidates = [cA, cB];

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < candidates.length; i++) {
        const parsed = tryJSON((candidates[i] || '').trim());
        if (parsed) return normalizePayload(parsed);
    }

    return null;
}

const HiddenReport: React.FC = () => {
    const payload = useMemo<Payload | null>(() => decodePayloadFromQuery(), []);
    const pptVersion = payload.powerpoint_template;
    return (
        <>
            {pptVersion === 'Linear_report' && <LinearReport payload={payload as LinearPayload} />}
            {pptVersion === 'DSP_report' && <DSPReport payload={payload as DspPayload} />}
            {pptVersion === 'custom' && <CustomReport payload={payload as CustomPayload} />}
        </>
    );
};

export default HiddenReport;
